/**
 * #262: a control that is off because of something the user can change must
 * say why. A native `disabled` button swallows its `title` (Firefox never
 * shows it, Chromium only after a delay, touch and screen readers never), so
 * a user-fixable block renders as `aria-disabled` + `title` instead and the
 * click is ignored. Native `disabled` stays for in-flight states, where the
 * label already reads "Saving…" and nothing needs explaining.
 */

export type ButtonGate = {
  /** Native `disabled`: only while a call is in flight. */
  disabled: boolean;
  /** `aria-disabled`: blocked for a reason the user can fix. */
  ariaDisabled: boolean;
  /** Hover text: the reason when blocked, else the optional hint. */
  title: string | undefined;
  /** The click must be ignored (in flight or blocked). */
  blocked: boolean;
};

export function buttonGate({
  busy = false,
  reason = null,
  hint,
}: {
  busy?: boolean;
  reason?: string | null;
  hint?: string;
}): ButtonGate {
  if (busy) return { disabled: true, ariaDisabled: false, title: hint, blocked: true };
  if (reason) return { disabled: false, ariaDisabled: true, title: reason, blocked: true };
  return { disabled: false, ariaDisabled: false, title: hint, blocked: false };
}

/**
 * The first reason whose condition holds, else null. Order the pairs the way
 * the user should fix them (the first missing field first).
 */
export function firstReason(...checks: ReadonlyArray<readonly [boolean, string]>): string | null {
  for (const [blocked, reason] of checks) if (blocked) return reason;
  return null;
}
